import dayjs from 'dayjs'
import moment from 'moment'
import settings from 'popups/Settings'
import { getReminderTime } from '../modules/TodoItem'
import {
  localizedDateString_ddddMMMMDYYYY,
  localizedTimeString,
} from 'pages/calendar/utils/DateUtils'

/**
 * Todo 에서 날짜 포맷팅을 위한 유틸리티 함수를 모아두었다.
 */

/**
 * DueDate Selector 에서 dueDate 표시 포맷팅
 *
 * Examples:
 * - Today's date => "Today"
 * - Tomorrow's date => "Tomorrow"
 * - "2024-12-25" => "December 25, 2024"
 * - "2024-01-15" => "January 15, 2024"
 *
 * - Formats due dates with "Today", "Tomorrow", or localized date
 * - Examples: "Today", "Tomorrow", "December 25, 2024"
 *
 * @param dateStr - Date string or Date object to format
 * @param t - Translation function from react-i18next
 * @returns Formatted date string
 */
export function formatDueDate(
  dateStr: string | Date | null,
  t: (key: string, options?: any) => string,
): string {
  if (!dateStr) return ''

  const date = moment(dateStr)
  if (!date.isValid()) return dateStr.toString()

  const today = moment().startOf('day')
  const tomorrow = moment().add(1, 'day').startOf('day')
  const target = date.clone().startOf('day')

  if (target.isSame(today)) return t('todo.todolist.today')
  if (target.isSame(tomorrow)) return t('todo.todolist.tomorrow')

  // 그 외의 경우 settings의 dateFormat 사용
  const dateFormat = settings.getValue('general').dateFormat
  return dayjs(date.toDate()).format(dateFormat)
}

/**
 * Formats a reminder time for display in todo lists
 *
 * Examples:
 * - Same day reminder � "09:30 AM"
 * - Different day reminder � "Tomorrow, 09:30 AM"
 * - Past date reminder � "Dec 24, 09:30 AM"
 * - Relative reminder � "15 minutes before"
 *
 * @param reminder - Reminder alarm object
 * @param due - Due date of the todo
 * @param startDate - Start date of the todo
 * @param t - Translation function from react-i18next
 * @returns Formatted reminder string
 */
export function getReminderTimeStr(
  reminder: any,
  due: string | Date | null,
  startDate: string | Date | null,
  t: (key: string, options?: any) => string,
): string {
  const reminderTime = getReminderTime(
    reminder,
    due?.toString(),
    startDate instanceof Date ? startDate : undefined,
  )
  if (!reminderTime) return ''

  // 리마인더 시간의 날짜 부분과 due date의 날짜 부분을 비교
  const reminderDate = moment(reminderTime).startOf('day')
  const dueDate = due ? moment(due).startOf('day') : null

  // due date가 없거나 리마인더 날짜와 due date가 다른 경우, 날짜와 시간을 모두 표시
  if (!dueDate || !reminderDate.isSame(dueDate)) {
    const today = moment().startOf('day')
    const tomorrow = moment().add(1, 'day').startOf('day')

    let dateStr = ''
    if (reminderDate.isSame(today)) {
      dateStr = t('todo.todolist.today')
    } else if (reminderDate.isSame(tomorrow)) {
      dateStr = t('todo.todolist.tomorrow')
    } else {
      // settings의 dateFormat 사용
      const dateFormat = settings.getValue('general').dateFormat
      dateStr = dayjs(reminderTime).format(dateFormat)
    }

    const timeStr = localizedTimeString(reminderTime)
    return `${dateStr}, ${timeStr}`
  }

  // due date와 같은 날짜인 경우 시간만 표시 (기존 동작)
  return localizedTimeString(reminderTime)
}

/**
 * Checks if a due date is overdue (past current time)
 *
 * Examples:
 * - "2024-01-01" (past date) => true
 * - "2025-12-31" (future date) => false
 *
 * @param dateStr - Date string or Date object to check
 * @returns True if date is in the past, false otherwise
 */
export function isOverdue(dateStr: Date | null): boolean {
  if (!dateStr) return false
  const dueDate = moment(dateStr).toDate()
  if (isNaN(dueDate.getTime())) return false

  // Compare only dates, ignoring time
  const dueDateOnly = moment(dueDate).startOf('day').toDate()
  const todayOnly = moment().startOf('day').toDate()

  return dueDateOnly < todayOnly
}

export function isOverdueReminder(reminderTime: Date | null): boolean {
  if (!reminderTime) return false
  const now = moment().toDate()
  return reminderTime < now
}

/**
 * Formats -  due date selectors (short format)
 * TodoEditor DueDateSelector 에서 사용.
 *
 * Examples:
 * - null/undefined => "Add due date"
 * - "2024-12-25" => "Dec 25, 2024"
 * - "2024-01-15" => "Jan 15, 2024"
 *
 * @param date - Date object or null
 * @param t - Translation function from react-i18next
 * @returns Formatted date string or default text
 */
export function formatDisplayDate(
  date: Date | null,
  t: (key: string, options?: any) => string,
): string {
  if (!date) return t('todo.editor.dueDate')

  // moment를 사용하여 today/tomorrow 판단
  const targetDate = moment(date)
  if (!targetDate.isValid()) return date.toString()

  const today = moment().startOf('day')
  const tomorrow = moment().add(1, 'day').startOf('day')
  const target = targetDate.clone().startOf('day')

  // 오늘이면 "Today" 표시
  if (target.isSame(today)) return t('todo.editor.today')
  // 내일이면 "Tomorrow" 표시
  if (target.isSame(tomorrow)) return t('todo.editor.tomorrow')

  // 그 외의 경우 settings의 dateFormat 사용
  const dateFormat = settings.getValue('general').dateFormat
  return dayjs(date).format(dateFormat)
}

/**
 * 생성일 표시 포맷팅
 * TodoEditor CreatedDateDisplay 에서 사용.
 *
 * Examples:
 * - Yesterday's date => "yesterday"
 * - Today's date => "on Mon, Dec 25, 2024"
 * - "2024-01-01" => "on Mon, Jan 1, 2024"
 *
 * @param date - Created date
 * @param t - Translation function from react-i18next
 * @returns Formatted created date string
 */
export function formatCreatedDate(
  date: Date | string,
  t: (key: string, options?: any) => string,
): string {
  const createdDayjs = dayjs(date)
  const today = dayjs()
  const yesterday = today.subtract(1, 'day')

  // Check if it's yesterday
  if (createdDayjs.isSame(yesterday, 'day')) {
    return t('todo.editor.yesterday')
  }

  // Check if it's today or before yesterday
  if (createdDayjs.isSame(today, 'day') || createdDayjs.isBefore(yesterday, 'day')) {
    // Format as "on Mon, Jun 6, 2026"

    const formattedDate = localizedDateString_ddddMMMMDYYYY(createdDayjs.toDate())
    // const formattedDate = createdDayjs.locale('en').format('ddd, MMM D, YYYY')
    return t('todo.editor.onDate', { date: formattedDate })
  }

  // For any other case (shouldn't happen for created dates but just in case)
  // const formattedDate = createdDayjs.locale('en').format('ddd, MMM D, YYYY')
  const formattedDate = localizedDateString_ddddMMMMDYYYY(createdDayjs.toDate())
  return t('todo.editor.onDate', { date: formattedDate })
}

/**
 * 완료일 표시 포맷팅 (날짜 + 시간)
 * TodoDetails completedDate 표시에서 사용.
 * settings의 dateFormat, timeFormat을 사용하여 날짜와 시간을 표시.
 *
 * Examples:
 * - Yesterday's date => "yesterday, 09:30 AM"
 * - Today's date => "12/25/2024, 09:30 AM" (settings에 따라)
 * - "2024-01-01" => "01/01/2024, 09:30 AM" (settings에 따라)
 *
 * @param date - Completed date
 * @param t - Translation function from react-i18next
 * @returns Formatted completed date string with time using settings format
 */
export function formatCompletedDate(
  date: Date | string,
  t: (key: string, options?: any) => string,
): string {
  const completedDayjs = dayjs(date)
  const today = dayjs()
  const yesterday = today.subtract(1, 'day')

  // Get localized time string
  const timeStr = localizedTimeString(completedDayjs.toDate())

  // Check if it's yesterday
  if (completedDayjs.isSame(yesterday, 'day')) {
    return `${t('todo.editor.yesterday')}, ${timeStr}`
  }

  // Check if it's today or before yesterday
  if (completedDayjs.isSame(today, 'day') || completedDayjs.isBefore(yesterday, 'day')) {
    // Use settings dateFormat like formatDueDate does
    const dateFormat = settings.getValue('general').dateFormat
    const dateStr = dayjs(completedDayjs).format(dateFormat)
    return `${dateStr}, ${timeStr}`
  }

  // For any other case (shouldn't happen for completed dates but just in case)
  const dateFormat = settings.getValue('general').dateFormat
  const dateStr = dayjs(completedDayjs).format(dateFormat)
  return `${dateStr}, ${timeStr}`
}

/**
 * 반복 일정 표시 포맷팅
 * RecurrenceEditor 에서 사용.
 *
 * Examples:
 * - "2024-12-25" -> "2024-12-25"
 * - Date object -> "2024-12-25"
 * - null -> "Select date"
 *
 * @param date - Date to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export function formatRecurrenceDate(date: Date | string | null): string {
  if (!date) return 'Select date'
  return dayjs(date).format('YYYY-MM-DD')
}
